import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Switch } from '@mui/material'
import { useDealId } from 'pages/DecisionEditPage/hooks/useDealId'
import {
  CurrencyConvertLineItem,
  useCurrencyConvertEditPage,
} from 'queries/useCurrencyConvertEditPage'
import { useStyles } from '../../styles/ToggleComponent.styles'

interface CurrencyToggleProps {
  dealCurrency?: string
  checked?: boolean
  onCurrencyDataChange?: (
    plData: CurrencyConvertLineItem[] | null,
    pnData: CurrencyConvertLineItem[] | null,
    isUSD: boolean
  ) => void
  onLoadingChange?: (isLoading: boolean) => void
}

export const CurrencyToggle = ({
  dealCurrency = 'EUR',
  checked,
  onCurrencyDataChange,
  onLoadingChange,
}: CurrencyToggleProps) => {
  const { classes } = useStyles()
  const [isUSD, setIsUSD] = useState(checked ?? false)
  const dealId = useDealId()
  const isUSDRef = useRef(isUSD)

  const { getCurrencyConversion } = useCurrencyConvertEditPage()

  useEffect(() => {
    if (checked !== undefined) {
      setIsUSD(checked)
      isUSDRef.current = checked
    }
  }, [checked])

  const handleToggleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked
      setIsUSD(checked)
      isUSDRef.current = checked

      if (checked && dealId) {
        onLoadingChange?.(true)
        try {
          const [plResult, pnResult] = await Promise.all([
            getCurrencyConversion({
              variables: {
                dealId,
                lineTypeCode: 'PL',
                toCurrencyCode: 'UD',
              },
            }),
            getCurrencyConversion({
              variables: {
                dealId,
                lineTypeCode: 'PN',
                toCurrencyCode: 'UD',
              },
            }),
          ])

          if (isUSDRef.current) {
            onCurrencyDataChange?.(
              plResult.data?.currencyConvertDecisionEditPage ?? null,
              pnResult.data?.currencyConvertDecisionEditPage ?? null,
              true
            )
          }
        } catch (error) {
          console.error('Error fetching currency conversion:', error)
        } finally {
          if (isUSDRef.current) {
            onLoadingChange?.(false)
          }
        }
      } else {
        onCurrencyDataChange?.(null, null, false)
        onLoadingChange?.(false)
      }
    },
    [dealId, getCurrencyConversion, onCurrencyDataChange, onLoadingChange]
  )

  return (
    <Box className={classes.toggleContainer}>
      <span className={classes.label}>{dealCurrency}</span>
      <Switch
        checked={isUSD}
        onChange={handleToggleChange}
        className={classes.switch}
        slotProps={{ input: { 'aria-label': `toggle between ${dealCurrency} and USD view` } }}
      />
      <span className={classes.label}>USD</span>
    </Box>
  )
}
